import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import { useEffect, useState } from "react";
import { buscarContaProtheusFiltros } from "../../services/api-conciliacao.service";

const ContaProtheusFormSchema = z
  .object({
    descricao: z.string(),
    conta_contabil: z.string(),
  })
  .required({
    descricao: true,
    conta_contabil: true,
  });

type ContaProtheusFormData = z.infer<typeof ContaProtheusFormSchema>;

type FiltroVinculadoRow = {
  id_conciliacao_filtro_vinculo_conta?: number;
  banco_label?: string | null;
  filtro?: string;
};

type ContaProtheusFormProps = {
  contaProtheusValues: Record<string, unknown> & {
    id_conciliacao_conta_contabil?: number;
    descricao?: string;
    conta_contabil?: string;
  };
  onSubmit(data: unknown): void;
  onClose(event: unknown): void;
};

export default function ContaProtheusForm({
  contaProtheusValues,
  onSubmit,
  onClose,
}: ContaProtheusFormProps) {
  const [filtrosVinculados, setFiltrosVinculados] = useState<FiltroVinculadoRow[]>([]);
  const [loadingFiltros, setLoadingFiltros] = useState(false);

  const idConta = contaProtheusValues.id_conciliacao_conta_contabil;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContaProtheusFormData>({
    mode: "onChange",
    resolver: zodResolver(ContaProtheusFormSchema),
    defaultValues: {
      descricao: contaProtheusValues.descricao ?? "",
      conta_contabil: contaProtheusValues.conta_contabil ?? "",
    },
  });

  useEffect(() => {
    reset({
      descricao: contaProtheusValues.descricao ?? "",
      conta_contabil: contaProtheusValues.conta_contabil ?? "",
    });
  }, [contaProtheusValues, reset]);

  useEffect(() => {
    let cancelled = false;
    if (idConta == null || idConta === undefined) {
      setFiltrosVinculados([]);
      setLoadingFiltros(false);
      return;
    }
    setLoadingFiltros(true);
    (async () => {
      try {
        const res = await buscarContaProtheusFiltros(idConta);
        const list = (res.filtrosVinculados ?? []) as FiltroVinculadoRow[];
        if (!cancelled) setFiltrosVinculados(list);
      } catch {
        if (!cancelled) setFiltrosVinculados([]);
      } finally {
        if (!cancelled) setLoadingFiltros(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idConta]);

  const handleCloseForm = (event: unknown) => {
    onClose(event);
  };

  const submit = (data: ContaProtheusFormData) => {
    const body: Record<string, unknown> = {
      contaContabil: {
        id_conciliacao_conta_contabil: contaProtheusValues.id_conciliacao_conta_contabil,
        descricao: data.descricao,
        contaContabil: data.conta_contabil,
      },
    };
    onSubmit(body);
  };

  const renderFiltrosSection = () => {
    if (idConta == null || idConta === undefined) {
      return (
        <Typography variant="body2" color="text.secondary" className={styles.filtrosVazio}>
          Salve a conta para visualizar os filtros vinculados.
        </Typography>
      );
    }
    if (loadingFiltros) {
      return (
        <Box className={styles.filtrosLoading}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Carregando filtros…
          </Typography>
        </Box>
      );
    }
    if (filtrosVinculados.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" className={styles.filtrosVazio}>
          Nenhum filtro vinculado a esta conta.
        </Typography>
      );
    }
    return (
      <TableContainer component={Paper} variant="outlined" className={styles.tabelaFiltros}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Banco</TableCell>
              <TableCell>Filtro</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtrosVinculados.map((row) => (
              <TableRow key={row.id_conciliacao_filtro_vinculo_conta ?? `${row.banco_label}-${row.filtro}`}>
                <TableCell>{row.banco_label ?? "—"}</TableCell>
                <TableCell>{row.filtro ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="conta_contabil"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Conta Protheus"
                    error={Boolean(errors.conta_contabil)}
                    helperText={errors.conta_contabil?.message}
                    placeholder={"Conta Protheus"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="descricao"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Descrição"
                    error={Boolean(errors.descricao)}
                    helperText={errors.descricao?.message}
                    placeholder={"Descrição"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className={styles.filtrosTitulo}>
                Filtros vinculados
              </Typography>
              {renderFiltrosSection()}
            </Grid>
          </Grid>
        </Box>
        <Box>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </form>
  );
}
